<template>
    <div class="store-settings" @click.prevent.stop>

        <transition name="slide" appear v-for="(setting,index) in settings" :key="setting.label + '_' + index">
            <div class="setting" v-if="setting.delay">
                <div class="line">
                    <label>{{setting.label}}</label>
                    <input v-model="setting.value" type="number" :disabled="saving"/>
                </div>
                <div class="description">
                    {{ setting.description }}
                </div>
                <button class="save" @click="setting.click" :disabled="saving" v-if="canmanage">Save</button>
            </div>
        </transition>

    </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StoreItemSettings',
  props: ['id','canmanage'],
  data() {
    return {
      saving: false,
      settings: [
        {
          id: 'default',
          delay: false,
          condition: 'def',
          label: 'Current Default',
          description: 'The occupancy default will be set to the selected value, and applied during resets.',
          value: undefined,
          click: this.setOccupancyDefault,
        },
        {
          id: 'occupancy',
          delay: false,
          condition: 'occupancy',
          label: 'Current Occupancy',
          description: 'The current occupancy will be set to the selected value when you click save.',
          value: undefined,
          click: this.setOccupancy,
        },
        {
          id: 'limit',
          delay: false,
          condition: 'limit',
          label: 'Current Limit',
          description: 'The current limit will be set to the selected value when you click save.',
          value: undefined,
          click: this.setLimit,
        }
      ]
    };
  },

  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      axios.get(`${process.env.VUE_APP_API_BASE}/occupancy-limits?api_key=${this.$store.getters.key}&format=json&site_id=${this.id}`)
        .then((res) => {
          let index = this.settings.findIndex(s => s.id === 'limit');
          this.settings[index].value = res.data.results[0].limit;
        });

      axios.get(`${process.env.VUE_APP_API_BASE}/b/v1/intellicount/override/default?site_id=${this.id}&api_key=${this.$store.getters.key}`)
        .then((res) => {
          if (res.data) {
            let index = this.settings.findIndex(s => s.id === 'default');
            this.settings[index].value = res.data[res.data.length - 1].count;
          }
        });

      for (let i = 0; i < this.settings.length; i++) {
        setTimeout(() => {
          this.settings[i].delay = true;
        },i * 200);
      }
    },

    setOccupancyDefault() {
      if (this.canmanage) {
        this.saving = true;
        axios.post(`${process.env.VUE_APP_API_BASE}/b/v1/intellicount/override/default?api_key=${this.$store.getters.key}`, {
          'site_id': this.id,
          'count': this.settings.find(s => s.id === 'default').value
        })
          .then(() => {
            this.$notify.success('Occupancy default updated');
            this.saving = false;
            this.$emit('fetch');
          })
          .catch(() => {
            this.$notify.error('Could not update occupancy default');
            this.saving = false;
          });
      }
    },

    setOccupancy() {
      if (this.canmanage) {
        this.saving = true;
        axios.post(`${process.env.VUE_APP_API_BASE}/b/v1/intellicount/override?api_key=${this.$store.getters.key}`, {
          'timestamp': null,
          'site_id': this.id,
          'count': this.settings.find(s => s.id === 'occupancy').value
        })
          .then(() => {
            this.$notify.success('Occupancy updated');
            this.$emit('fetch');
            this.saving = false;
          })
          .catch(() => {
            this.$notify.error('Could not update occupancy');
            this.saving = false;
          });
      }
    },

    setLimit() {
      if (this.canmanage) {
        this.saving = true;
        axios.post(`${process.env.VUE_APP_API_BASE}/occupancy-limits?site_id=${this.id}&api_key=${this.$store.getters.key}`, {
          limit: parseInt(this.settings.find(s => s.id === 'limit').value),
        })
          .then((res) => {
            let index = this.settings.findIndex(s => s.id === 'limit');
            this.settings[index].value = res.data.limit;
            this.$notify.success('Limit updated');
            this.$emit('fetch');
            this.saving = false;
          })
          .catch(() => {
            this.$notify.error('Could not update limit');
            this.saving = false;
          });
      }
    }

  }
};
</script>

<style scoped lang="scss">

.store-settings {
    position: relative;
    height: 100%;
    width: 100%;
    font-size: 1em;
    cursor: default;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 1em;
    grid-row-gap: 1em;

    .setting {
        position: relative;
        background: #FFF;
        padding: 1em;
        box-sizing: border-box;
        box-shadow: var(--shadow);
        border-radius: .5em;
        display: flex;
        flex-flow: column;

        .line {
            label {
                flex-grow: 1;
                color: initial;
                opacity: .8;
            }

            input {
                width: 100%;
                flex-grow: 1;
                margin: .5em 0;
                text-align: center;
                transition: all cubic-bezier(.25, .8, .25, 1) .3s;
                border: none;
                background: #fafafa;
                box-shadow: var(--shadow-inset);
                border-radius: 8px;
                line-height: 40px;
                color: #4e4e4e;
            }
        }

        .description {
            color: #929293;
            font-size: .8em;
            line-height: 1.25;
            flex-grow: 1;
        }

        button {
            padding: .5em 1.25em;
            border-radius: 8px;
            border: none;
            cursor: pointer;
            width: fit-content;
            align-self: flex-end;
            background-color: #0684C7;
            color: #FFF;
            opacity: .75;
            box-shadow: var(--shadow);
            transition: all cubic-bezier(.25, .8, .25, 1) .3s;

            &:disabled {
                opacity: .5;
                cursor: default;
            }

            &:hover {
                box-shadow: 0 5px 10px rgba(0, 0, 0, 0.22);
            }
        }

        &:hover {
            button {
                opacity: 1;
            }
        }
    }
}

@media only screen and (max-width: 1280px) {
    .store-settings {
        .setting {
            min-height: 248px;
            margin: 0;
        }
    }
}
@media only screen and (max-width: 640px) {
    .store-settings {
        grid-template-columns: repeat(1,1fr);
        .setting {
            //min-height: auto;
            min-height: 200px;
            display: flex;
            //flex: 1;
            flex-flow: column;

            button {
                margin-top: 1em;
            }
        }
    }
}

</style>